/* eslint-disable no-console */
import { Injectable, NgZone } from '@angular/core';
import { signOut as amplifySignOut, fetchAuthSession, getCurrentUser, fetchUserAttributes } from '@aws-amplify/auth';
import { Signer } from '@aws-amplify/core/internals/utils';
import AWS, { CognitoIdentityCredentials } from 'aws-sdk/global';
import { environment } from '../../environments/environment';
import S3 from 'aws-sdk/clients/s3';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { UserService } from './user.service';
import { APIService } from './api.service';
import * as Sentry from '@sentry/angular-ivy';
import { AuthenticatorService } from '@aws-amplify/ui-angular';

export interface Params {
  headers: unknown;
  data: unknown;
  method: string;
  url: string;
}

export interface Credentials {
  secret_key: string;
  access_key: string;
  session_token: string;
  identityId?: string;
}

// set your region and service here
export interface ServiceInfo {
  region: string;
  service: string;
}

@Injectable({
  providedIn: 'root',
})
export class AmplifyService {
  private refreshingSession = false;
  constructor(
    private readonly ngZone: NgZone,
    private readonly userService: UserService,
    private readonly apiService: APIService,
    private readonly authenticatorService: AuthenticatorService,
  ) {}

  public async getCurrentCredentials(authSession: any = null): Promise<Credentials> {
    if (!authSession) {
      authSession = await fetchAuthSession();
    }
    if (
      JSON.stringify(authSession.credentials).includes(
        'Unauthenticated access is not supported for this identity pool.',
      )
    ) {
      throw new Error('Unauthenticated access is not supported for this identity pool.');
    }
    return {
      session_token: authSession.credentials?.sessionToken ?? '',
      access_key: authSession.credentials?.accessKeyId ?? '',
      secret_key: authSession.credentials?.secretAccessKey ?? '',
      identityId: authSession.identityId,
    };
  }

  public async getSignedRequest(params: Params, credential: Credentials, serviceInfo: ServiceInfo): Promise<any> {
    // TODO deprecated
    return Signer.sign(params, credential, serviceInfo);
  }

  public async signOut(reload = true): Promise<void> {
    if (!navigator.onLine) {
      return;
    }
    Sentry.captureMessage('AmplifyService-signOut', {
      tags: {
        online: navigator.onLine,
        networkSpeed: (navigator as any).connection?.downlink,
        rtt: (navigator as any).connection?.rtt,
        saveData: (navigator as any).connection?.saveData,
        networkType: (navigator as any).connection?.type,
        networkEffectiveType: (navigator as any).connection?.effectiveType,
        version: environment.version,
      },
    });
    try {
      await amplifySignOut();
      if (reload) {
        this.ngZone.run(() => {
          if (navigator.onLine) {
            window.location.reload();
          }
        });
      }
    } catch (error) {
      console.error(error);
      Sentry.captureException(error, {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
    }
  }

  public async refreshSession(forceRefresh = true): Promise<any> {
    if (this.refreshingSession) {
      return;
    }
    this.refreshingSession = true;
    if (!this.apiService.onLine || document.hidden) {
      if (localStorage.getItem('authData')) {
        this.apiService.user = JSON.parse(localStorage.getItem('authData') ?? '');
        Sentry.setUser(this.apiService.user || null);
      }
      if (localStorage.getItem('userIdentityId')) {
        this.apiService.userIdentityId = localStorage.getItem('userIdentityId') ?? '';
      }
      this.refreshingSession = false;
      return null;
    }
    let authSession = undefined;
    try {
      if (this.authenticatorService.authStatus === 'authenticated') {
        authSession = await fetchAuthSession({ forceRefresh });
        if (authSession) {
          await this.updateAWS(authSession);
          const user = await getCurrentUser();
          const attributes = await fetchUserAttributes();
          this.apiService.user = { username: user.username, attributes: attributes };
          Sentry.setUser(this.apiService.user);
          if (this.apiService.user) {
            localStorage.setItem(
              'authData',
              JSON.stringify({
                username: user.username,
                attributes: { ...attributes },
              }),
            );
          }
          const jwtTokens = authSession.tokens;
          const decoded = jwtDecode<JwtPayload>(jwtTokens?.idToken?.toString() ?? '');
          this.userService.decodedToken = decoded;
          localStorage.setItem('decodedToken', JSON.stringify(decoded));
          await this.storeCredentials(authSession);
        }
      }
      console.warn('authStatus', this.authenticatorService.authStatus);
    } catch (error) {
      if (this.authenticatorService.error) {
        Sentry.captureMessage('AmplifyService-refreshSession: ' + this.authenticatorService.error, {
          tags: {
            online: navigator.onLine,
            networkSpeed: (navigator as any).connection?.downlink,
            rtt: (navigator as any).connection?.rtt,
            saveData: (navigator as any).connection?.saveData,
            networkType: (navigator as any).connection?.type,
            networkEffectiveType: (navigator as any).connection?.effectiveType,
            version: environment.version,
          },
        });
      }
      Sentry.captureException(error, {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
      // on some devices if data economy is activated we sometimes get 504
      // from POST to https://cognito-idp.us-east-1.amazonaws.com/
      setTimeout(() => {
        this.ngZone.run(() => {
          if (navigator.onLine) {
            window.location.reload();
          }
        });
      }, 100);
      return null;
    } finally {
      this.refreshingSession = false;
    }
    return authSession;
  }

  private async storeCredentials(authSession: any): Promise<void> {
    try {
      const credentials = await this.getCurrentCredentials(authSession);
      this.apiService.userIdentityId = credentials.identityId ?? '';
      localStorage.setItem('userIdentityId', credentials.identityId ?? '');
    } catch (error) {
      console.error(error);
      Sentry.captureMessage('AmplifyService-storeCredentials error', {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
      Sentry.captureException(error, {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
    }
  }

  private async updateAWS(authSession: any): Promise<void> {
    const jwtTokens = authSession.tokens;
    if (!AWS.config?.credentials) {
      AWS.config.update({
        region: environment.AWS_REGION,
        credentials: new AWS.CognitoIdentityCredentials({
          IdentityPoolId: environment.IDENTITY_POOL_ID,
          Logins: {
            [`cognito-idp.${environment.AWS_REGION}.amazonaws.com/${environment.USER_POOL_ID}`]:
              jwtTokens?.idToken?.toString() ?? '',
          },
        }),
      });
    }
    // refresh credentials
    if (AWS.config.credentials instanceof CognitoIdentityCredentials && AWS.config.credentials.needsRefresh()) {
      await AWS.config.credentials.getPromise();
      await AWS.config.credentials.refreshPromise();
    }
    new S3({ region: environment.AWS_REGION, signatureVersion: 'v4', credentials: AWS.config.credentials });
  }
}
